import React, { useEffect, useState } from "react";
import { employeesApi, usersApi, errorMessage } from "../adminApi";
import { useAdminToast } from "../components/Toast";
import { Card, Table, Badge, Button, Modal, Loading, ErrorState, PageHeader, Select, Pagination, MapLocation } from "../components/ui";

const PAGE_SIZE = 20;

/** Float jam Odoo (8.5) → "08:30"; string "HH:MM" diteruskan apa adanya. */
function floatToHHMM(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "string" && v.includes(":")) return v;
  const f = Number(v);
  if (!Number.isFinite(f)) return null;
  let h = Math.floor(f);
  let m = Math.round((f - h) * 60);
  if (m === 60) { h += 1; m = 0; }
  return `${String(h % 24).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export default function EmployeesPage() {
  const toast = useAdminToast();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [assigning, setAssigning] = useState(null); // employee row
  const [viewing, setViewing] = useState(null); // employee row (detail read-only)

  const load = async (pageArg = 0, searchArg = search) => {
    setLoading(true);
    setError(null);
    try {
      const res = await employeesApi.list({ search: searchArg || undefined, limit: PAGE_SIZE, offset: pageArg * PAGE_SIZE });
      setRows(res.rows || []);
      setTotal(res.total ?? (res.rows || []).length);
      setPage(pageArg);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    { key: "nik", header: "NIK / Badge", render: (r) => r.badge_number || r.nik || "-" },
    { key: "name", header: "Nama", render: (r) => <span className="font-medium text-slate-800">{r.full_name}</span> },
    { key: "dept", header: "Departemen", render: (r) => r.department || "-" },
    { key: "job", header: "Jabatan", render: (r) => r.job_title || "-" },
    { key: "email", header: "Email", render: (r) => r.email || "-" },
    { key: "phone", header: "Telepon", render: (r) => r.phone || "-" },
    {
      key: "act",
      header: "",
      render: (r) => (
        <div className="flex gap-1.5 justify-end">
          <Button size="sm" variant="ghost" onClick={() => setViewing(r)}>Detail</Button>
          <Button size="sm" variant="ghost" onClick={() => setAssigning(r)}>Tautkan Akun</Button>
        </div>
      ),
    },
  ];

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Karyawan"
        subtitle="Data master karyawan (read-only, bersumber dari sync Odoo)."
      />

      <div className="flex gap-2 mb-3 shrink-0">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(0, search)}
          placeholder="Cari nama / NIK…"
          className="w-full max-w-xs rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
        />
        <Button variant="secondary" onClick={() => load(0, search)}>Cari</Button>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {loading ? (
          <Loading />
        ) : error ? (
          <ErrorState message={error} onRetry={() => load(page)} />
        ) : (
          <>
            <Card className="flex-1 min-h-0 overflow-hidden">
              <Table columns={columns} rows={rows} empty="Belum ada karyawan." />
            </Card>
            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPage={(p) => load(p, search)} />
          </>
        )}
      </div>

      <EmployeeDetailModal employee={viewing} onClose={() => setViewing(null)} />
      <AssignUserModal employee={assigning} onClose={() => setAssigning(null)} onDone={() => { setAssigning(null); load(page); }} />
    </div>
  );
}

function DInfo({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg p-2 border border-slate-100">
      <div className="text-[11px] text-slate-400">{label}</div>
      <div className="text-sm text-slate-800 break-words">{value || "-"}</div>
    </div>
  );
}

/**
 * Detail karyawan READ-ONLY — fokus lokasi kerja & jadwal (sumber Odoo).
 * Jam fallback work_location dipakai saat jadwal karyawan flexible/tak ada
 * (lihat rule_source "Lokasi" di halaman Absensi). Edit dilakukan di Odoo.
 */
function EmployeeDetailModal({ employee, onClose }) {
  if (!employee) return null;
  const e = employee;
  const loc = e.work_location && typeof e.work_location === "object" ? e.work_location : null;
  const ci = loc ? floatToHHMM(loc.checkin_time) : null;
  const co = loc ? floatToHHMM(loc.checkout_time) : null;

  return (
    <Modal open={!!employee} onClose={onClose} title={`Detail • ${e.full_name}`} wide>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
        <DInfo label="Nama" value={e.full_name} />
        <DInfo label="NIK / Badge" value={e.badge_number || e.nik} />
        <DInfo label="Departemen" value={e.department} />
        <DInfo label="Jabatan" value={e.job_title} />
        <DInfo label="Tipe Absensi" value={e.attendance_type} />
        <DInfo label="Status" value={e.active ? "Aktif" : "Nonaktif"} />
      </div>

      {/* Jadwal kerja (Tier-1) */}
      <div className="text-xs font-medium text-slate-500 mb-2">Jadwal Kerja</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
        <DInfo label="Jadwal" value={e.work_schedule_name || "Belum ditautkan"} />
        <DInfo
          label="Tipe"
          value={e.work_schedule_id ? (e.work_schedule_flexible ? "Flexible" : "Fix") : "-"}
        />
      </div>

      {/* Lokasi kerja (Tier-2 fallback) */}
      <div className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-2">
        Lokasi Kerja (fallback jam)
        {loc?.default_work_mode && <Badge color="gray">{loc.default_work_mode}</Badge>}
      </div>
      {loc ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
            <DInfo label="Nama Lokasi" value={loc.name} />
            <DInfo label="Jam Masuk (fallback)" value={ci} />
            <DInfo label="Jam Pulang (fallback)" value={co} />
            <DInfo label="Radius Geofence" value={loc.radius != null ? `${loc.radius} m` : "-"} />
          </div>
          {loc.latitude != null && loc.longitude != null && (
            <MapLocation label="Titik Lokasi Kerja" lat={loc.latitude} lng={loc.longitude} address={loc.name} />
          )}
        </>
      ) : (
        <div className="text-sm text-slate-400 bg-slate-50 border border-slate-100 rounded-lg p-3">
          Belum ada data lokasi kerja dari Odoo.
        </div>
      )}

      <div className="text-[11px] text-slate-400 mt-4">
        Data lokasi & jadwal bersumber dari Odoo (read-only). Ubah di Odoo lalu sync;
        override jadwal manual tersedia di halaman <b>Jadwal Kerja</b>.
      </div>
    </Modal>
  );
}

function AssignUserModal({ employee, onClose, onDone }) {
  const toast = useAdminToast();
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!employee) return;
    setUserId("");
    usersApi.list().then((res) => setUsers(res.rows || res || [])).catch(() => setUsers([]));
  }, [employee]);

  if (!employee) return null;

  const submit = async () => {
    if (!userId) return;
    setBusy(true);
    try {
      await employeesApi.assignUser(employee.id, userId);
      toast.success("Akun ditautkan ke karyawan");
      onDone();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={!!employee}
      onClose={onClose}
      title={`Tautkan Akun → ${employee.full_name}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={busy || !userId}>{busy ? "Menautkan…" : "Tautkan"}</Button>
        </>
      }
    >
      <p className="text-sm text-slate-500 mb-3">
        Pilih akun login yang akan dipetakan ke karyawan ini (mengisi NIK/odoo_id untuk integrasi).
      </p>
      <Select label="Akun (username)" value={userId} onChange={(e) => setUserId(e.target.value)}>
        <option value="">— pilih akun —</option>
        {users.map((u) => (
          <option key={u.id} value={u.id}>{u.username} ({u.role})</option>
        ))}
      </Select>
    </Modal>
  );
}
